import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

/**
 * Multi-select tag filter for list pages.
 * Shows a dropdown with available tag definitions for the entity type.
 * Returns selected tag IDs to the parent via onChange.
 *
 * Props:
 *   practiceId - current practice ID
 *   entityType - 'patient' | 'provider' | 'appointment_type' | 'appointment'
 *   selected   - array of selected tag definition IDs
 *   onChange   - callback(selectedTagIds[])
 */
function TagFilterDropdown({ practiceId, entityType, selected = [], onChange }) {
  const [definitions, setDefinitions] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (practiceId && entityType) {
      api.getTagDefinitions(practiceId, entityType)
        .then(res => { if (res.success) setDefinitions(res.data || []); })
        .catch(err => console.error('Failed to load tag definitions:', err));
    }
  }, [practiceId, entityType]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function toggleTag(tagId) {
    const next = selected.includes(tagId)
      ? selected.filter(id => id !== tagId)
      : [...selected, tagId];
    onChange(next);
  }

  function clearAll() {
    onChange([]);
  }

  if (definitions.length === 0) return null;

  const selectedDefs = definitions.filter(d => selected.includes(d.id));

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: selected.length > 0 ? '#eff6ff' : '#fff',
          border: `1px solid ${selected.length > 0 ? '#3b82f6' : '#d1d5db'}`,
          borderRadius: 6, padding: '5px 10px', fontSize: 13,
          cursor: 'pointer', color: selected.length > 0 ? '#1d4ed8' : '#374151',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
        Tags{selected.length > 0 && ` (${selected.length})`}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: 4,
          minWidth: 200, maxHeight: 280, overflowY: 'auto', marginTop: 4,
        }}>
          {selected.length > 0 && (
            <div
              onClick={clearAll}
              style={{
                padding: '6px 10px', fontSize: 12, color: '#ef4444', cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9', marginBottom: 2,
              }}
            >
              Clear all filters
            </div>
          )}
          {definitions.map(def => {
            const active = selected.includes(def.id);
            return (
              <div
                key={def.id}
                onClick={() => toggleTag(def.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', cursor: 'pointer', borderRadius: 4,
                  background: active ? '#f1f5f9' : 'transparent',
                  fontSize: 13,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = active ? '#f1f5f9' : 'transparent'}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: def.color, flexShrink: 0,
                }} />
                <span style={{ flex: 1 }}>{def.name}</span>
                {active && <span style={{ color: '#3b82f6', fontWeight: 700 }}>&#10003;</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Show selected tag names as small pills below the button */}
      {selectedDefs.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
          {selectedDefs.map(def => (
            <span key={def.id} style={{
              fontSize: 10, padding: '1px 5px', borderRadius: 8,
              background: def.color + '20', color: def.color,
              border: `1px solid ${def.color}40`,
            }}>
              {def.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default TagFilterDropdown;
